import { useEffect, useState } from "react";
import api from "../../lib/api";
import { EmptyState, Loading, PageHeader } from "../../components/shared";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { formatDuration, formatTimestamp } from "../expert/ExpertSessions";
import { Clock3, Search } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["ALL", "ACTIVE", "ENDED"];

export default function AdminExpertSessions() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("ALL");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);

  const load = (nextStatus = status, nextQuery = query) => {
    api
      .get("/admin/expert-sessions", {
        params: { status: nextStatus === "ALL" ? "" : nextStatus, q: nextQuery },
      })
      .then((response) => setRows(response.data))
      .catch(() => setRows([]));
  };

  useEffect(() => {
    load("ALL", "");
  }, []);

  const openDetail = async (sessionId) => {
    try {
      const response = await api.get(`/admin/expert-sessions/${sessionId}`);
      setDetail(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load consultation session");
    }
  };

  if (!rows) {
    return <Loading />;
  }

  return (
    <div>
      <PageHeader
        title="Consultation Sessions"
        subtitle="Read-only oversight of trusted consultation usage records."
      />
      <div className="mb-5 flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            data-testid="admin-session-search"
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && load()}
            placeholder="Search session, appointment, request or society"
            value={query}
          />
        </div>
        <select
          className="h-10 rounded-md border px-3 text-sm"
          data-testid="admin-session-status"
          onChange={(event) => {
            setStatus(event.target.value);
            load(event.target.value, query);
          }}
          value={status}
        >
          {FILTERS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <Button data-testid="admin-session-search-button" onClick={() => load()} type="button">
          <Search className="mr-2 h-4 w-4" />
          Search
        </Button>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={Clock3}
          subtitle="Started consultations will appear here."
          title="No consultation sessions"
        />
      ) : (
        <div className="overflow-x-auto border" data-testid="admin-sessions-table-wrap">
          <table className="w-full text-sm" data-testid="admin-sessions-table">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="p-3">Session</th>
                <th className="p-3">Appointment</th>
                <th className="p-3">Society</th>
                <th className="p-3">Expert</th>
                <th className="p-3">Service</th>
                <th className="p-3">Actual start</th>
                <th className="p-3">Duration</th>
                <th className="p-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  className="cursor-pointer border-t hover:bg-muted/30"
                  data-testid={`admin-session-${row.session_id}`}
                  key={row.session_id}
                  onClick={() => openDetail(row.session_id)}
                >
                  <td className="p-3 font-medium">{row.display_id}</td>
                  <td className="p-3 text-xs">{row.appointment_id}</td>
                  <td className="p-3">{row.society_name || row.society_id}</td>
                  <td className="p-3">{row.expert_snapshot?.name || row.expert_id}</td>
                  <td className="p-3">{row.service_snapshot?.name || row.service_id}</td>
                  <td className="p-3 text-xs">{formatTimestamp(row.actual_start_utc)}</td>
                  <td className="p-3">{formatDuration(row.actual_duration_seconds)}</td>
                  <td className="p-3">
                    <span className="bg-emerald-100 px-2 py-1 text-xs text-emerald-800">
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={Boolean(detail)} onOpenChange={(open) => !open && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="admin-session-detail">
          <DialogHeader>
            <DialogTitle className="font-display">Session {detail?.display_id}</DialogTitle>
            <DialogDescription>Read-only trusted session record.</DialogDescription>
          </DialogHeader>
          {detail && (
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <Row label="Session status" value={detail.status} />
              <Row label="Appointment ID" value={detail.appointment_id} />
              <Row label="Request ID" value={detail.consultation_request_id} />
              <Row label="Society" value={detail.society_name || detail.society_id} />
              <Row label="Expert" value={detail.expert_snapshot?.name || detail.expert_id} />
              <Row label="Service" value={detail.service_snapshot?.name || detail.service_id} />
              <Row label="Scheduled" value={formatTimestamp(detail.scheduled_start_utc)} />
              <Row label="Actual start" value={formatTimestamp(detail.actual_start_utc)} />
              <Row label="Actual end" value={formatTimestamp(detail.actual_end_utc)} />
              <Row label="Actual duration" value={formatDuration(detail.actual_duration_seconds)} />
            </dl>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div data-testid={`admin-session-detail-${label.toLowerCase().replaceAll(" ", "-")}`}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-all font-medium">{value || "—"}</dd>
    </div>
  );
}